import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, type CraneSwingHazard } from '../../../hazards/HazardDefinition';
import { hazardSocket } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, moduleSides, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';
import { CRANE_SWING } from './SEG_CRANE_SWING_20M';

/**
 * Swinging crane loads closer together the further the run goes (user 2026-10-05: "der Abstand der Pendelgewichte von den
 * Kränen, je nachdem wie weit ich gelaufen bin, näher und nicht mehr so weit auseinander"). SEG_CRANE_SWING_20M carries
 * one load per 20 m module (up to 3000 m); these longer modules carry several loads at a tighter, even spacing – also
 * across module borders in a chain run (first load half a gap in, last half a gap before the end):
 *
 *   SEG_CRANE_SWING_60M         from 1200 m   4 loads, 15 m apart (module lengths are 20/40/60 m) – from 4000 m longer runs
 *   SEG_CRANE_SWING_60M_TIGHT   from 2500 m   5 loads, 12 m apart – from 5000 m longer runs
 *
 * Each load is a hazard of its own with its own tower crane beside the track (alternating sides, the crane 6 m behind its
 * load like SEG_CRANE_SWING_20M) and its own layout – lingering over the left / right lane or sweeping through the
 * centre. The populator checks every load's key line against the one before (SequenceValidator) and leaves out a load
 * that would not fit: reaction and two lane changes take ≈ 0.78 s, 15 m at top speed (16 m/s) take 0.94 s.
 */
const W = WORLD_CONFIG;
const STRAIGHT = 20;
/** Layouts per module (each a random-looking but fixed mix of the loads' layouts). */
const VARIANTS = 10;
/** Half the tower crane's cross base (m) – no roadside bay within it; the diagonal of the turned cross (3 · √2). */
const CRANE_BASE_HALF = 4.3;

interface RunSpec {
  readonly id: string;
  readonly displayName: string;
  readonly length: number;
  readonly loads: number;
  readonly minDistance: number;
  readonly maxDistance?: number;
  readonly chain: readonly (readonly [number, number])[];
  readonly chainFrom?: readonly { readonly distance: number; readonly lengths: readonly (readonly [number, number])[] }[];
}

/** Small deterministic generator for the fixed layouts (the same module always has the same variants). */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/**
 * Lanes the loads block, in order (user 2026-10-05 late: "nicht gleichzeitig immer gleich … durcheinander … dass man nicht
 * gerade durchrennen kann"): a shuffled order of 0/1/2 that repeats, so every three loads in a row close each lane once
 * and two loads in a row never hang over the same lane – no lane stays free, every load swings in its own rhythm.
 */
export function mixedLanes(count: number, random: () => number): number[] {
  const order = [0, 1, 2];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return Array.from({ length: count }, (_, k) => order[k % 3]!);
}

function craneSwingRun(spec: RunSpec): SegmentDefinition {
  const gap = spec.length / spec.loads;
  const slots = Array.from({ length: spec.loads }, (_, k) => {
    const z = gap / 2 + k * gap;
    const craneSide = k % 2 === 0 ? 1 : -1;
    return { k, z, craneSide, socket: `SOCKET_EVT_CRANE_SWING_${k + 1}` };
  });
  const swing = (k: number, lane: number, sweep?: 1 | -1): CraneSwingHazard => {
    const slot = slots[k]!;
    const tag = lane === 1 ? (sweep === 1 ? 'L1_RIGHT' : 'L1_LEFT') : `L${lane}`;
    return {
      id: `HAZ_SWING_${k + 1}_${tag}`,
      kind: HazardKind.CraneSwing,
      socketId: slot.socket,
      lane,
      ...(sweep ? { sweep } : {}),
      z: slot.z,
      crane: { x: slot.craneSide * CRANE_SWING.crane.x, z: Math.min(spec.length - 0.5, slot.z + (CRANE_SWING.crane.z - CRANE_SWING.load.z)) },
    };
  };
  const hazards = slots.flatMap(({ k }) => [swing(k, 0), swing(k, 2), swing(k, 1, 1), swing(k, 1, -1)]);
  const random = lcg(spec.length * 31 + spec.loads);
  const variants: ObstacleVariant[] = Array.from({ length: VARIANTS }, (_, v) => ({
    id: `SWING_RUN_${v + 1}`,
    weight: 1,
    placements: [],
    // the centre-lane load sweeps through to either side (a different moment of its swing)
    hazards: mixedLanes(spec.loads, random).map((lane, k) => (lane === 1 ? swing(k, 1, random() < 0.5 ? 1 : -1) : swing(k, lane)).id),
  }));
  // No roadside bay where a tower crane stands: its 6 m cross base (half 3 m) plus the bay's own depth, on its side
  const sides = moduleSides(spec.length, { groundProps: [] });
  const decorationSockets = sides.decorationSockets.filter(
    (socket) =>
      !slots.some(
        (s) =>
          (socket.side === 'LEFT' ? -1 : 1) === s.craneSide &&
          Math.abs(socket.position.z - (s.z + CRANE_SWING.crane.z - CRANE_SWING.load.z)) < CRANE_BASE_HALF + socket.maxFootprint.depth / 2 + 0.5,
      ),
  );
  return {
    id: spec.id,
    kind: SegmentKind.Straight,
    displayName: spec.displayName,
    length: spec.length,
    width: W.trackWidth,
    visualAssetId: 'SEG_STRAIGHT_20M',
    extraVisuals: Array.from({ length: spec.length / STRAIGHT - 1 }, (_, i) => ({ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: STRAIGHT * (i + 1) } })),
    entry: entryConnector(),
    exits: [straightExit(spec.length)],
    lanes: standardLanes(W),
    obstacleSpawnPoints: [],
    obstacleRowLayouts: { sparse: [], dense: [] },
    obstacleVariants: variants,
    hazards,
    collectibleSpawnPoints: collectibleLanes(W, 1, spec.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),
    decorationSockets,
    advertisingSlots: sides.advertisingSlots,
    dynamicEventSockets: [...sides.dynamicEventSockets, ...slots.map((s) => hazardSocket(s.socket, 0, s.z, DynamicEventType.CraneLoadSwing))],
    generation: {
      weight: 0.2,
      minDistance: spec.minDistance,
      ...(spec.maxDistance === undefined ? {} : { maxDistance: spec.maxDistance }),
      minSegmentsAfterJunction: 1,
      // the same family as the crane area and the single swing module: never right behind them
      family: 'CRANE',
      chain: { lengths: spec.chain, ...(spec.chainFrom ? { from: spec.chainFrom } : {}) },
      tags: ['crane', 'hazard', 'lane-change'],
    },
  };
}

// user 2026-10-05 late: "ein recht kurzer Abstand … unter den schwingenden Kränen … dass es wirklich nicht leicht wird" –
// the 15 m runs from 1200 m (was 3000 m) in longer series, from 2500 m also 12 m apart; a load whose key line comes too
// soon after the one before is still left out by the validator, so every run stays passable
export const SEG_CRANE_SWING_60M = craneSwingRun({
  id: 'SEG_CRANE_SWING_60M',
  displayName: 'Pendelkräne dicht 60 m',
  length: 60,
  loads: 4,
  minDistance: 1200,
  chain: [
    [1, 0.3],
    [2, 0.45],
    [3, 0.25],
  ],
  chainFrom: [{ distance: 4000, lengths: [[2, 0.35], [3, 0.4], [4, 0.25]] }],
});

export const SEG_CRANE_SWING_60M_TIGHT = craneSwingRun({
  id: 'SEG_CRANE_SWING_60M_TIGHT',
  displayName: 'Pendelkräne sehr dicht 60 m',
  length: 60,
  loads: 5,
  minDistance: 2500,
  chain: [
    [1, 0.35],
    [2, 0.45],
    [3, 0.2],
  ],
  chainFrom: [{ distance: 5000, lengths: [[2, 0.4], [3, 0.4], [4, 0.2]] }],
});